import type { WebSockets } from '@pushflow/types'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ETableLimit } from '../../../types/enums'
import { Button } from '../../atoms'
import { calcTableLimit } from '../../organisms/TablesList/utils'
import { ModeIcon } from '..'
import { Money } from '../Money'
import {
  body,
  brandColorVariants,
  header,
  joinButton,
  joinButtonColumn,
  limitBadgeBase,
  modesBlock,
  players,
  tableBlockBase,
  tableBlockFull,
  tableInfo,
  values,
} from './index.css'

export const TableListElement = (
  props: WebSockets.TableType & {
    onClickSit: () => void
  },
) => {
  const { t } = useTranslation()
  const [tableLimit, setTableLimit] = useState<ETableLimit>()
  const isFull = props.playerCount >= props.maxPlayers
  const modes = {
    doubleBoard: props.isDoubleBoardPossible ? 'active' : null,
    bombPot:
      props.bombPotMin &&
      props.bombPotMax &&
      props.bombPotFrequency &&
      props.bombPotActivateDoubleBoard !== null
        ? {
            min: props.bombPotMin,
            max: props.bombPotMax,
            frequency: props.bombPotFrequency,
            activateDoubleBoard: props.bombPotActivateDoubleBoard,
            state: 'active',
          }
        : null,
  }

  useEffect(() => {
    if (
      props.currency === 'USDT' ||
      props.currency === 'TON' ||
      props.currency === 'COIN'
    ) {
      const limit = calcTableLimit(props.currency, props.bigBlind)
      if (limit) {
        setTableLimit(limit)
      }
    }
  }, [props.bigBlind, props.currency])

  return (
    <div
      className={clsx(
        tableBlockBase,
        isFull && tableBlockFull,
        brandColorVariants[tableLimit || 'default'],
      )}
    >
      <div className={limitBadgeBase}>
        {props.type === 'PUBLIC' && tableLimit !== undefined
          ? t(`lobby.limit.${tableLimit}`)
          : props.code.toUpperCase()}
      </div>
      <div className={tableInfo}>
        <div className={header}>
          <span>{t('lobby.table.blinds')}</span>
          <span>{t('lobby.table.buyIn')}</span>
          <span>{t('lobby.table.players')}</span>
          {modes.doubleBoard || modes.bombPot ? (
            <span className={modesBlock}>
              {modes.bombPot ? (
                <ModeIcon
                  mode='bombPot'
                  params={modes.bombPot}
                />
              ) : null}
              {modes.doubleBoard ? <ModeIcon mode='doubleBoard' /> : null}
            </span>
          ) : null}
        </div>
        <div className={body}>
          <div className={values}>
            <span>
              <Money
                withIcon={true}
                iconFirst={true}
                currency={props.currency}
                inBigBlinds={false}
              >
                {props.smallBlind}
              </Money>
            </span>
            <span>
              <Money
                withIcon={true}
                iconFirst={true}
                currency={props.currency}
                inBigBlinds={false}
              >
                {props.bigBlind}
              </Money>
            </span>
          </div>
          <div className={values}>
            <span>
              <Money
                withIcon={true}
                iconFirst={true}
                currency={props.currency}
                inBigBlinds={false}
              >
                {props.minBuyIn}
              </Money>
            </span>
            <span>
              <Money
                withIcon={true}
                iconFirst={true}
                currency={props.currency}
                inBigBlinds={false}
              >
                {props.maxBuyIn}
              </Money>
            </span>
          </div>
          <div className={players}>
            {props.playerCount}/{props.maxPlayers}
          </div>
          <div className={joinButtonColumn}>
            <Button
              variant='primary'
              className={joinButton}
              analytic={{
                name: 'join',
                params: {
                  currency: props.currency,
                  id: props.id,
                },
              }}
              onClick={props.onClickSit}
            >
              {t('lobby.table.button.join')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
